import type {
  ArrowOpacityState,
  Dimensions,
} from '@components/ArtBoard/Arrows/types'
import { useClickOutside } from '@lib/hooks'
import {
  type ArrowGridPoint,
  type Offset,
  ProjectActions,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { commitProjectRequest } from '@storeOld/actionCreators'
import {
  CanvasActionTypes,
  setSelectedArrowPointId,
} from '@storeOld/reducers/canvas/canvas'
import type React from 'react'
import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'

import { useScaleZoom } from '@/hooks'
import type { AppState } from '@/types'
import { clsJoin } from '@/utils'
import css from './styles.module.css'

interface Props extends ArrowGridPoint {
  arrowId: string
  disabled?: boolean
  opacityState: ArrowOpacityState
  isArrowSelected?: boolean
}

const ArrowLineHandler: React.FC<Props> = ({
  arrowId,
  id,
  offset: { x, y },
  opacityState,
  isArrowSelected,
}) => {
  const dispatch = useDispatch()

  const isSelected = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.arrow.selectedArrowGridPointId === id
  )

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )
  const { scale } = useScaleZoom()

  const arrowsHide = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.arrow.hideArrows
  )

  const [initialState, setInitialState] = useState<Dimensions | null>(null)
  const [offset, setOffset] = useState<Offset>({ x, y })

  const arrow = useSelector(migrationProjectSelector.getProjectArrow(arrowId))
  const color = useSelector(
    migrationProjectSelector.getProjectArrowColorHex(arrowId)
  )

  const changeOffset = (x: number, y: number) => {
    setOffset({ x, y })

    dispatch(
      ProjectActions.arrows.gridPointUpdateOffset({
        id: arrowId,
        item: { id, offset: { x, y } },
      })
    )
  }

  const onPointerDown = (e: React.MouseEvent) => {
    if (initialState) {
      return
    }

    onClick()

    const mouseX = e.clientX
    const mouseY = e.clientY

    const offsetX = x ?? 0
    const offsetY = y ?? 0

    setInitialState({
      mouseX,
      mouseY,
      offsetX,
      offsetY,
    })

    changeOffset(offsetX, offsetY)
  }

  const onPointerMove = (e: React.MouseEvent) => {
    if (initialState) {
      const dx = initialState.mouseX - e.clientX
      const dy = initialState.mouseY - e.clientY

      const x = initialState.offsetX - dx / scale
      const y = initialState.offsetY - dy / scale

      changeOffset(x, y)
    }
  }

  const onPointerUp = () => {
    setInitialState(null)

    setTimeout(onClick)

    dispatch(
      commitProjectRequest(
        ProjectActions.arrows.gridPointUpdateOffset({
          id: arrowId,
          item: { id, offset: { x, y } },
        })
      )
    )
  }

  const onClick = () => {
    // @todo implement actions
    // @ts-expect-error
    dispatch(setSelectedArrowPointId(id))

    dispatch({
      type: CanvasActionTypes.SET_SELECTED_ARROW_ID,
      payload: {
        id: arrowId,
      },
    })
  }

  const clickOutside = useClickOutside<HTMLDivElement>(
    isSelected as boolean,
    () => {},
    () => {
      if (!isReadOnly) {
        onClick()
      }
    }
  )

  const pointerLeft = isSelected ? offset.x : x
  const pointerTop = isSelected ? offset.y : y
  const pointerStyle = {
    '--arrow-pointer-color': color,
    left: pointerLeft,
    top: pointerTop,
  } as React.CSSProperties

  if (!arrow) {
    return null
  }

  const portal = document.getElementById('printed-node') as HTMLElement

  const { y: top, x: left } = isSelected ? offset : { x, y }

  // the portal copy scales like #tree does, so the same offset lands on the
  // in-place pointer above — no legend to correct for since the layer holding
  // that pointer is anchored to #tree as well
  const handlerStyle = {
    '--arrow-pointer-color': color,
    left,
    top,
  } as React.CSSProperties

  const cn = clsJoin(
    css.container,
    'xarrow-label',
    css[opacityState],
    (isSelected || isArrowSelected) && css.selected,
    isReadOnly && css.read_only,
    arrowsHide && css.hide
  )

  return (
    <>
      <div className={cn} style={pointerStyle} id={id} {...clickOutside}>
        <div
          className={css.control}
          data-arrow-point-control-id={id}
          onPointerUp={onPointerUp}
          onPointerMove={onPointerMove}
          onPointerDown={onPointerDown}
        />
      </div>
      {isArrowSelected &&
        portal &&
        ReactDOM.createPortal(
          <div
            className={css.z_container}
            style={{ transform: `scale(${scale})` }}
          >
            <div className={css.handler} style={handlerStyle}>
              <div
                className={clsJoin(css.control, isSelected && css.selected)}
                data-arrow-point-control-id={id}
                onPointerUp={onPointerUp}
                onPointerMove={onPointerMove}
                onPointerDown={onPointerDown}
              />
            </div>
          </div>,
          portal
        )}
    </>
  )
}

export default ArrowLineHandler
